import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "kc_admin_token";

export default function Subscribers() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/subscribers`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setData(null);
        return;
      }
      if (res.ok) setData(await res.json());
    } catch {
      setError("Could not load the list — please try again");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/admin-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        const { token: t } = await res.json();
        localStorage.setItem(TOKEN_KEY, t);
        setToken(t);
        setPassword("");
      } else {
        setError("Incorrect password");
      }
    } catch {
      setError("Something went wrong — please try again");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  return (
    <main
      data-testid="subscribers-page"
      className="min-h-screen w-full bg-white flex flex-col items-center justify-center px-6 py-10"
    >
      <p className="kc-tagline text-sm tracking-[0.35em] uppercase text-neutral-900">
        Katrina-Catrina
      </p>

      {!token ? (
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="mt-10 w-64 flex flex-col items-center gap-6"
        >
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="kc-form-text w-full bg-transparent border-0 border-b border-neutral-300 focus:border-neutral-900 outline-none py-2 text-sm tracking-[0.12em] text-center text-neutral-900 placeholder:text-neutral-400 transition-colors"
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="kc-form-text text-xs tracking-[0.3em] uppercase text-neutral-900 border-b border-neutral-900 py-2 hover:opacity-50 transition-opacity disabled:opacity-40"
          >
            {loading ? "Checking" : "View list"}
          </button>
          {error && (
            <p
              data-testid="admin-login-error"
              className="kc-form-text text-xs tracking-[0.15em] uppercase text-red-800"
            >
              {error}
            </p>
          )}
        </form>
      ) : (
        <div className="mt-10 w-full max-w-md flex flex-col items-center">
          <p
            data-testid="subscriber-count"
            className="kc-established text-xs tracking-[0.3em] uppercase text-neutral-500"
          >
            {data ? `${data.count} on the list` : "Loading"}
          </p>
          <ul data-testid="subscribers-list" className="mt-8 w-full divide-y divide-neutral-200">
            {data?.subscribers.map((s) => (
              <li
                key={s.id}
                data-testid={`subscriber-row-${s.email}`}
                className="flex items-center justify-between gap-4 py-3"
              >
                <span className="kc-form-text text-sm tracking-[0.08em] text-neutral-900 truncate">
                  {s.email}
                </span>
                <span className="kc-form-text shrink-0 text-xs tracking-[0.12em] text-neutral-400">
                  {new Date(s.timestamp).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </li>
            ))}
          </ul>
          {data && data.count === 0 && (
            <p className="kc-form-text mt-8 text-sm text-neutral-400">No signups yet</p>
          )}
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="kc-form-text mt-10 text-xs tracking-[0.3em] uppercase text-neutral-400 hover:text-neutral-900 transition-colors"
          >
            Sign out
          </button>
        </div>
      )}

      <Link
        data-testid="back-home-link"
        to="/"
        className="kc-form-text mt-12 text-xs tracking-[0.3em] uppercase text-neutral-300 hover:text-neutral-900 transition-colors"
      >
        Back to page
      </Link>
    </main>
  );
}
